import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import './MainResult.css';
import ImageZoomOverlay from './ImageZoomOverlay';
import CategoryNav from './CategoryNav';
import CategoryNavMobile from './CategoryNavMobile';
import PromptBar from './PromptBar';
import MobilePromptBar from './MobilePromptBar';
import UserSettingsModal from './UserSettingsModal';
import UserSettingsModalMobile from './UserSettingsModalMobile';
import ConfirmDialog from './ConfirmDialog';
import { supabase } from '../lib/supabase';
import { selectPendingPromptData } from '../store/selectors';
import { setPendingPromptData } from '../store/promptSlice';

const MainResult = ({ images, onBack, onRegenerate, isLoading, totalExpected, onImageUpdate }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { requestId, imageId } = useParams();
  const userInputs = useSelector(selectPendingPromptData);
  const [zoomImage, setZoomImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryConfirm, setShowCategoryConfirm] = useState(false);
  const [pendingCategoryIndex, setPendingCategoryIndex] = useState(null);
  const initialProjectRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
    });
  }, []);


  useEffect(() => {
    if (!userInputs?.availableTags) return;

    const currentProject = `${userInputs?.projectName || ''}-${userInputs?.idea || ''}`;
    if (initialProjectRef.current === currentProject) return;

    initialProjectRef.current = currentProject;

    const selectedTags = userInputs?.selectedTag
      ? userInputs.selectedTag.map(tag => {
        return Object.values(tag)[0];
      })
      : [];

    const categoriesWithActive = (userInputs?.availableTags || []).map(category => {
      const isActive = selectedTags.includes(category);
      let fieldId = null;
      if (userInputs?.fields && Array.isArray(userInputs.fields)) {
        const matchingField = userInputs.fields.find(field =>
          field.options && Array.isArray(field.options) && field.options.includes(category)
        );
        if (matchingField) {
          fieldId = matchingField.id;
        }
      }
      return {
        name: category,
        active: isActive,
        fieldId: fieldId
      };
    });

    setCategories(categoriesWithActive);
  }, [userInputs]);

  const baseFields = userInputs?.fields || [];
  const assets = userInputs?.assets || [];

  // Helper function to convert camelCase to title text
  const camelCaseToTitle = (str) => {
    if (!str) return '';
    return str
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
      .trim();
  };

  const fields = [...baseFields];
  const hasDynamicInputField = baseFields.some(f => f.id === 'dynamic_input');

  if (!hasDynamicInputField && userInputs?.dynamicInput && typeof userInputs.dynamicInput === 'object') {
    Object.entries(userInputs.dynamicInput).forEach(([key, value]) => {
      const exists = baseFields.some(f => f.id === key || f.display === camelCaseToTitle(key));
      if (!exists && value !== null && value !== undefined && value !== '') {
        fields.push({
          id: key,
          display: camelCaseToTitle(key),
          type: 'text',
          placeholder: `Enter ${camelCaseToTitle(key)}...`
        });
      }
    });
  }

  const excludedFields = ['fields', 'projectName', 'idea', 'assets', 'availableTags', 'selectedTag', 'dynamicInput'];
  const formData = {};
  if (userInputs) {
    Object.entries(userInputs).forEach(([key, value]) => {
      if (!excludedFields.includes(key)) {
        formData[key] = value;
      }
    });

    if (userInputs.dynamicInput && typeof userInputs.dynamicInput === 'object') {
      Object.entries(userInputs.dynamicInput).forEach(([key, value]) => {
        formData['dynamic_input'] = value;
      });
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate mock image data with specific figma ratios to fill grid properly
  const displayImages = useMemo(() => {
    if (!images || images.length === 0) return [];

    return images.map((img, index) => {
      // Use 4 specific aspect ratios for 4-column layout
      // Rotate the ratios for each row so each column gets variety
      const rowIndex = Math.floor(index / 4); // Which row (0, 1, 2...) - 4 columns now
      const colIndex = index % 4; // Position in row (0, 1, 2, 3)
      // Shift the ratio based on row and column for variety
      const ratioType = (rowIndex + colIndex) % 4;

      let aspectRatio;

      switch (ratioType) {
        case 0:
          aspectRatio = '362/180'; // Wide landscape (2:1)
          break;
        case 1:
          aspectRatio = '357/301'; // Portrait (1.19:1)
          break;
        case 2:
          aspectRatio = '313/301'; // Almost square (1.04:1)
          break;
        case 3:
          aspectRatio = '4/3'; // Standard 4:3
          break;
        default:
          aspectRatio = '4/3';
      }

      return {
        ...img,
        aspectRatio,
        isAd: img.isAd,
        link: img.link,
        title: img.title
      };
    });
  }, [images]);

  // Handle imageId from URL - open overlay for specific image
  useEffect(() => {
    if (!imageId) {
      setZoomImage(null);
      return;
    }

    if (!displayImages?.length) return;

    // Check if it's an ad format: ad-{index}
    if (imageId.startsWith('ad-')) {
      const adIndex = parseInt(imageId.replace('ad-', ''), 10);
      if (!isNaN(adIndex) && adIndex >= 0 && displayImages[adIndex]?.isAd) {
        setZoomImage(displayImages[adIndex]);
        return;
      }
    }

    // Try to find image by UUID first
    const imageByUuid = displayImages.find(img => img.id === imageId);
    if (imageByUuid) {
      setZoomImage(imageByUuid);
      return;
    }

    // Fallback to index-based lookup
    const imageIndex = parseInt(imageId, 10);
    if (!isNaN(imageIndex) && imageIndex >= 0 && imageIndex < displayImages.length) {
      setZoomImage(displayImages[imageIndex]);
    }
  }, [imageId, displayImages]);

  const handlePromptBarRegenerate = (updatedFormData) => {
    if (!userInputs) return;

    const presetDynamicFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material', 'style'];

    const newDynamicInput = userInputs.dynamicInput && typeof userInputs.dynamicInput === 'object'
      ? { ...userInputs.dynamicInput }
      : {};

    if (updatedFormData.dynamic_input !== undefined && updatedFormData.dynamic_input !== null && updatedFormData.dynamic_input !== '') {
      const dynamicInputField = userInputs.fields?.find(f => f.id === 'dynamic_input');
      if (dynamicInputField && dynamicInputField.display) {
        const dynamicKey = dynamicInputField.display
          .replace(/\s+(.)/g, (_, c) => c.toUpperCase())
          .replace(/\s/g, '')
          .replace(/^(.)/, (_, c) => c.toLowerCase());
        newDynamicInput[dynamicKey] = updatedFormData.dynamic_input;
      } else {
        const existingKeys = Object.keys(newDynamicInput);
        if (existingKeys.length > 0) {
          newDynamicInput[existingKeys[0]] = updatedFormData.dynamic_input;
        } else if (userInputs.dynamicInput && typeof userInputs.dynamicInput === 'object') {
          // If newDynamicInput is empty but userInputs.dynamicInput exists, get keys from there
          const firstKey = Object.keys(userInputs.dynamicInput)[0];
          if (firstKey) {
            newDynamicInput[firstKey] = updatedFormData.dynamic_input;
          }
        }
      }
    }

    const preservedFields = {
      projectName: userInputs.projectName,
      idea: userInputs.idea,
      fields: userInputs.fields
    };

    const updatedData = {
      ...preservedFields,
      ...Object.fromEntries(
        presetDynamicFields
          .filter(field => updatedFormData[field] !== undefined)
          .map(field => [field, updatedFormData[field]])
      ),
      ...(Object.keys(newDynamicInput).length > 0
        ? { dynamicInput: newDynamicInput }
        : (userInputs.dynamicInput ? { dynamicInput: userInputs.dynamicInput } : {})
      ),
      ...(updatedFormData.assets !== undefined && { assets: updatedFormData.assets }),
      ...(updatedFormData.availableTags !== undefined && { availableTags: updatedFormData.availableTags }),
      ...Object.fromEntries(
        presetDynamicFields
          .filter(field => updatedFormData[field] === undefined && userInputs[field] !== undefined)
          .map(field => [field, userInputs[field]])
      ),
      ...(updatedFormData.assets === undefined && userInputs.assets !== undefined && { assets: userInputs.assets }),
      ...(updatedFormData.availableTags === undefined && userInputs.availableTags !== undefined && { availableTags: userInputs.availableTags }),
      selectedTag: null
    };

    dispatch(setPendingPromptData(updatedData));
    if (onRegenerate) {
      onRegenerate(updatedData);
    }
  };

  // Helper to determine fieldId for a category by finding which field has this category name in its options
  const getFieldIdForCategory = (categoryName) => {
    if (!categoryName || !userInputs?.fields || !Array.isArray(userInputs.fields)) {
      return null;
    }

    const matchingField = userInputs.fields.find(field =>
      field.options && Array.isArray(field.options) && field.options.includes(categoryName)
    );

    return matchingField ? matchingField.id : null;
  };

  // Show confirm dialog when category is clicked
  const handleCategorySelect = (index) => {
    const selectedCat = categories[index];

    if (!selectedCat || !onRegenerate) return;

    let fieldId = selectedCat.fieldId;
    if (!fieldId) {
      fieldId = getFieldIdForCategory(selectedCat.name);
    }

    if (!fieldId) {
      console.warn('Could not determine fieldId for category:', selectedCat.name);
      return;
    }

    setPendingCategoryIndex(index);
    setShowCategoryConfirm(true);
  };

  // Confirm category selection and generate
  const handleConfirmCategorySelect = async () => {
    setShowCategoryConfirm(false);

    if (pendingCategoryIndex === null) return;

    const selectedCat = categories[pendingCategoryIndex];
    if (!selectedCat || !onRegenerate) return;

    let fieldId = selectedCat.fieldId;
    if (!fieldId) {
      fieldId = getFieldIdForCategory(selectedCat.name);
    }

    if (!fieldId) {
      console.warn('Could not determine fieldId for category:', selectedCat.name);
      return;
    }

    const presetDynamicFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material', 'style'];

    const fieldUpdates = {
      [fieldId]: [selectedCat.name],
      ...Object.fromEntries(
        presetDynamicFields
          .filter(field => userInputs[field] !== undefined)
          .map(field => [field, field === fieldId ? [selectedCat.name] : userInputs[field]])
      ),
      ...(userInputs.dynamicInput !== undefined && { dynamicInput: userInputs.dynamicInput }),
      ...(userInputs.assets !== undefined && { assets: userInputs.assets }),
      selectedTag: [{ [fieldId]: selectedCat.name }],
    };

    await onRegenerate(fieldUpdates);
    setPendingCategoryIndex(null);
  };

  // Cancel category selection
  const handleCancelCategorySelect = () => {
    setShowCategoryConfirm(false);
    setPendingCategoryIndex(null);
  };

  // Determine if project name looks auto-generated (e.g., Project-2026-01-13T05-25-41)
  const projectNameRaw = userInputs?.projectName;
  const isAutoProjectName = useMemo(() => {
    if (!projectNameRaw) return false;
    const name = projectNameRaw.trim();
    const autoPatterns = [
      /^project[-_\s]?\d{4}-\d{2}-\d{2}/i,          // Project-2026-01-13...
      /^project[-_\s]?\d{8}[-_\s]?\d{6}/i,         // Project-20260113-052541
      /^project[-_\s]?\d{10,}$/i                   // Project-<timestamp>
    ];
    return autoPatterns.some((p) => p.test(name));
  }, [projectNameRaw]);

  const displayProjectName = !isAutoProjectName && projectNameRaw ? projectNameRaw : null;

  return (
    <div className="main-result-container">
      {/* Header Top Section */}
      <div className="result-header-top">
        {/* Project Info Block */}
        <div className="result-header-block">
          <div className="result-logo">
            <img src="/KURA-logo.png" alt="KURA Logo" />
          </div>
          <div className="header-divider"></div>
          <div className="result-project-info">
            {displayProjectName && (
              <h2 className="result-project-name">{displayProjectName}</h2>
            )}
            <p className="result-project-scenario">{userInputs?.idea || 'No description'}</p>
          </div>
          <div className="header-divider"></div>
          <div
            className="header-add-icon"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
            title="Start a new project"
          >
            <img className="header-add-icon-img" src="/plus-icon.svg" alt="Plus Icon" />
          </div>
        </div>

        {/* Header Navigation */}
        <div className="result-header-navigation" ref={navRef}>
          {
            isMobile
              ? (
                <CategoryNavMobile
                  categories={categories}
                  onCategorySelect={handleCategorySelect}
                />
              )
              : (
                <CategoryNav
                  categories={categories}
                  onCategorySelect={handleCategorySelect}
                />
              )
          }
        </div>

        {/* User Profile */}
        {user && (
          <div
            className="result-user-profile"
            onClick={() => setShowUserSettingsModal(true)}
            style={{ cursor: 'pointer' }}
          >
            <img src="/user-profile.png" alt="User Profile" />
          </div>
        )}
      </div>

      {/* Main Grid with Masonry Layout */}
      <div className="result-grid">
        {/* Create columns for masonry effect - 4 on desktop, 2 on mobile */}
        {(isMobile ? [0, 1] : [0, 1, 2, 3]).map((colIndex) => (
          <div key={colIndex} className="result-grid-column">
            {displayImages.map((image, index) => {
              // Distribute images across columns in a row-by-row pattern
              // On desktop: index 0,1,2,3 go to columns 0,1,2,3, then 4,5,6,7 go to columns 0,1,2,3 again, etc.
              // On mobile: index 0,1 go to columns 0,1, then 2,3 go to columns 0,1 again, etc.
              const numColumns = isMobile ? 2 : 4;
              const columnToRender = index % numColumns;
              if (columnToRender === colIndex) {
                return (
                  <div
                    key={index}
                    className="grid-card"
                    style={{ aspectRatio: image.aspectRatio }}
                    onClick={() => {
                      setZoomImage(image);
                      if (requestId) {
                        if (image.isAd) {
                          // Use ad-{index} format for Amazon ad images
                          navigate(`/result/${requestId}/ad-${index}`);
                        } else if (image.id) {
                          // Use image.id (UUID) as imageId in URL for generated images
                          navigate(`/result/${requestId}/${image.id}`);
                        } else {
                          // Fallback to index if no UUID
                          navigate(`/result/${requestId}/${index}`);
                        }
                      }
                    }}
                  >
                    {/* Image loads progressively (printer-style) */}
                    <img
                      src={image.url}
                      alt={`Result ${index}`}
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>

      {/* Messages when loading is finished */}
      {!isLoading && images && images.length > 0 && (
        <div className="result-messages">
          <div className="result-message-primary">
            Want more? Keep an eye on our updates.
          </div>
          <div className="result-message-secondary">
            Currently, we are in the free demo phase with limited features. If you're interested in additional options, feel free to contact us at{' '}
            <a href="mailto:support@heykura.com" className="result-message-email">support@heykura.com</a>.
          </div>
        </div>
      )}

      {/* Loading Spinner for more images */}
      {isLoading && (
        <div className="loading-more-container">
          <div className="loading-spinner"></div>
        </div>
      )}

      {zoomImage && (
        <ImageZoomOverlay
          imageUrl={zoomImage.url}
          imageData={zoomImage}
          onClose={() => {
            setZoomImage(null);
            if (requestId) {
              navigate(`/result/${requestId}`);
            }
          }}
          onImageUpdate={onImageUpdate}
        />
      )}

      {showUserSettingsModal && (
        isMobile ? (
          <UserSettingsModalMobile
            user={user}
            onClose={() => setShowUserSettingsModal(false)}
          />
        ) : (
          <UserSettingsModal
            user={user}
            onClose={() => setShowUserSettingsModal(false)}
          />
        )
      )}

      {/* PromptBar - Dynamic field icons with edit capability */}
      {!showUserSettingsModal && fields.length > 0 && !isMobile && (
        <PromptBar
          fields={fields}
          formData={formData}
          onRegenerate={handlePromptBarRegenerate}
          assets={assets}
        />
      )}

      {/* MobilePromptBar - Mobile version */}
      {!showUserSettingsModal && fields.length > 0 && isMobile && (
        <MobilePromptBar
          fields={fields}
          formData={formData}
          onRegenerate={handlePromptBarRegenerate}
          assets={assets}
        />
      )}

      {/* Confirm Dialog for Category Selection */}
      <ConfirmDialog
        isOpen={showCategoryConfirm}
        onConfirm={handleConfirmCategorySelect}
        onCancel={handleCancelCategorySelect}
        title="Confirm Generation"
        message={`Are you sure you want to regenerate designs with these settings?`}
      />
    </div>
  );
};

export default MainResult;


